import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant } from "@/mds";
import { useAppStore } from "@/store";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
  syntaxTreeModule,
} from "@mem-labs/common-editor";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { NoteObservable } from "@/store/note/NoteObservable";
import { UNTITLED_COLLECTION_TITLE, UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { EventContext, trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  InstructionsEditor,
  InstructionsEditorFetchDataHandler,
  InstructionsEditorMentionItem,
} from "@/pages/integrations/instructions-editor";
import { chatInstructionsReducer } from "./chat-instructions-settings.reducer";
import { SaveStatus } from "./chat-instructions-settings.types";
import { toastModule } from "@/modules/toast";

const PLACEHOLDER_TEXT = `Example: "Always respond in bullet points" or "Use technical language for engineering topics" or "Keep responses concise, under 3 sentences when possible"`;

export const ChatInstructionsSettings = observer(() => {
  const { store } = useAppStore();
  const inputRef = useRef<MemCommonRichTextInputInstance>(null);

  const initialInstructions = store.spaceAccountSettings.chat.instructions;

  const initialHtml = useMemo(() => {
    return initialInstructions
      ? syntaxTreeModule.convertMdxToHtml({ mdxContent: initialInstructions })
      : "";
  }, [initialInstructions]);

  const [state, dispatch] = useReducer(chatInstructionsReducer, {
    originalValue: initialHtml,
    currentValue: initialHtml,
    saveStatus: SaveStatus.Idle,
    error: null,
    isInitializing: !!initialHtml,
  });

  // Track content for the editor separately to handle async loading
  const [editorContent, setEditorContent] = useState<string | undefined>(initialHtml || undefined);

  // When the store loads with saved instructions, sync to reducer and editor
  useEffect(() => {
    if (initialHtml && state.originalValue === "") {
      dispatch({ type: "INITIALIZE", value: initialHtml });
      setEditorContent(initialHtml);
    }
  }, [initialHtml, state.originalValue, editorContent]);

  const isDirty = state.currentValue !== state.originalValue;
  const isSaving = state.saveStatus === SaveStatus.Saving;

  const handleContentModified = useCallback(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, []);

  const handleFetchData = useCallback<
    InstructionsEditorFetchDataHandler<InstructionsEditorMentionItem>
  >(
    async ({ query, mentionSymbol }: { query: string; mentionSymbol?: string }) => {
      const suggestionType =
        mentionSymbol === "@"
          ? SearchSuggestionType.NOTE
          : mentionSymbol === "#"
            ? SearchSuggestionType.COLLECTION
            : undefined;

      if (!suggestionType) {
        return { data: [], isLoading: false };
      }

      if (!query) {
        return {
          data: (suggestionType === SearchSuggestionType.NOTE
            ? store.recentItems.sortedRecentNotesInteractedWithByMe
            : store.recentItems.sortedRecentCollectionsInteractedWithByMe
          )
            .filter(item => item.isAvailable)
            .slice(0, 5)
            .map(item => ({
              id: item.id,
              label:
                item.title ??
                (item instanceof NoteObservable ? UNTITLED_NOTE_TITLE : UNTITLED_COLLECTION_TITLE),
              kind:
                item instanceof NoteObservable
                  ? MemCommonMentionKind.Note
                  : MemCommonMentionKind.Collection,
            })),
          isLoading: false,
        };
      }

      const condition = (item: SearchSuggestion) => item.type === suggestionType;
      const suggestions = await store.search.forSuggestions(query, "mentions", condition, 5);

      if (!suggestions.length && suggestionType === SearchSuggestionType.COLLECTION) {
        const collectionId = uuidModule.generate();
        return {
          data: [
            {
              id: collectionId,
              label: "Create new collection",
              kind: "create-new-collection",
              onClick: async () => {
                const title = query;
                await store.collections.createCollection({
                  collectionId,
                  title,
                  eventContext: EventContext.EditorInline,
                });
                return title;
              },
            },
          ],
          isLoading: false,
        };
      }

      return {
        data: suggestions.map(suggestion => ({
          id: suggestion.modelId,
          label: suggestion.label,
          kind:
            suggestion.type === SearchSuggestionType.NOTE
              ? MemCommonMentionKind.Note
              : MemCommonMentionKind.Collection,
        })),
        isLoading: false,
      };
    },
    [store.search, store.recentItems, store.collections]
  );

  const handleSubmit = useCallback((payload: { htmlContent: string }) => {
    dispatch({ type: "SET_VALUE", value: payload.htmlContent, error: null });
  }, []);

  const handleCancel = useCallback(() => {
    dispatch({ type: "CANCEL" });
    setEditorContent(state.originalValue || undefined);

    // Reset the editor content to original value
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SetEditorContent,
      payload: { content: state.originalValue || "" },
    });
  }, [state.originalValue]);

  const handleSave = useCallback(async () => {
    dispatch({ type: "START_SAVING" });

    try {
      const mdxContent = syntaxTreeModule.convertHtmlToMdx({
        htmlContent: state.currentValue ?? "",
      });

      await store.spaceAccountSettings.updateSettingsSynchronously({
        chat: { instructions: mdxContent },
      });

      dispatch({ type: "SAVE_SUCCESS", value: state.currentValue });
      trackEvent(TrackedEvent.ChatGlobalInstructionsSave, {
        instructions: mdxContent,
      });
      toastModule.triggerToast({
        content: "Chat preferences saved",
      });
    } catch (_error) {
      dispatch({ type: "SAVE_ERROR", error: "Failed to save chat instructions" });
      toastModule.triggerToast({
        content: "Could not save chat instructions",
      });
    }
  }, [state.currentValue, store]);

  return (
    <StyledContainer>
      <StyledDescription variant={MDSTextVariant.ParagraphS}>
        Customize how Mem responds across all chats. Changes take effect in your next new chat
      </StyledDescription>

      <InstructionsEditor
        isDisabled={false}
        inputRef={inputRef}
        setContent={editorContent}
        editorDimensions={{
          minHeight: 136,
          maxHeight: 450,
        }}
        placeholder={PLACEHOLDER_TEXT}
        fetchData={handleFetchData}
        onContentModified={handleContentModified}
        onSubmit={handleSubmit}
        disableSetContent
      />

      <StyledButtonContainer>
        <MDSButton onClick={handleCancel} isDisabled={!isDirty || isSaving}>
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={!isDirty || isSaving}
        >
          Save preferences
        </MDSButton>
      </StyledButtonContainer>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 784px;
`;

const StyledDescription = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;
